import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Drawer,
  Form,
  Select,
  Input,
  Table,
  Spin,
  Popconfirm,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { BACKEND_URL } from "../../../config/config";
import { NotificationContext } from "../../context/NotificationWrapper";

const { Option } = Select;

const CALENDAR_START_YEAR = 2024;

// Utilisé pour l'affichage dans l'historique (les anciens types restent lisibles)
const PERIOD_TYPE_LABELS = {
  monthly: "Mensuel",
  semi_annual_s1: "Semestriel S1",
  semi_annual_s2: "Semestriel S2",
  annual: "Annuel",
};

const MONTH_NAMES = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Jui",
  "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc",
];

const PAYED_DOT_COLORS = { yes: "#52c41a", ongoing: "#fa8c16", no: "#ff4d4f" };
const PAYED_LABELS = { yes: "Payée", ongoing: "En cours", no: "Non payée" };
const VOR_LABELS = { green: "Vert", orange: "Orange", red: "Rouge" };

// Statut de paiement agrégé si plusieurs bills actives couvrent le mois : le pire l'emporte
function worstPayedStatus(bills) {
  const order = ["no", "ongoing", "yes"];
  return order.find((s) => bills.some((b) => b.payed === s)) || null;
}

// Calcule les mois couverts par une période (retourne un Set de "YYYY-MM")
function getCoveredMonthKeys(inputedDate, periodType) {
  const date = new Date(inputedDate * 1000);
  const year = date.getFullYear();
  const keys = new Set();

  if (periodType === "monthly") {
    const month = date.getMonth() + 1;
    keys.add(`${year}-${String(month).padStart(2, "0")}`);
  } else if (periodType === "semi_annual_s1") {
    for (let m = 1; m <= 6; m++) {
      keys.add(`${year}-${String(m).padStart(2, "0")}`);
    }
  } else if (periodType === "semi_annual_s2") {
    for (let m = 7; m <= 12; m++) {
      keys.add(`${year}-${String(m).padStart(2, "0")}`);
    }
  } else if (periodType === "annual") {
    for (let m = 1; m <= 12; m++) {
      keys.add(`${year}-${String(m).padStart(2, "0")}`);
    }
  }

  return keys;
}

function formatTs(ts) {
  if (!ts) return "";
  return new Date(ts * 1000).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function RegulPeriodsPage() {
  const { membershipCode } = useParams();
  const navigate = useNavigate();
  const { openNotification } = useContext(NotificationContext);

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [declarations, setDeclarations] = useState([]);
  const [billAttestations, setBillAttestations] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [regulPeriods, setRegulPeriods] = useState([]);
  const [history, setHistory] = useState([]);
  const [reasons, setReasons] = useState({});
  const [selectableReasons, setSelectableReasons] = useState({});
  const [vorStatus, setVorStatus] = useState(null);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRegul, setEditingRegul] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null); // {year, month}
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form] = Form.useForm();

  const watchedPeriodType = Form.useWatch("periodType", form);

  useEffect(() => {
    fetchData();
  }, [membershipCode]);

  const fetchData = () => {
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/admin/regul-periods/${membershipCode}`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        setCompany(res.data.company);
        setDeclarations(res.data.declarations);
        setBillAttestations(res.data.billAttestations ?? []);
        setAttestations(res.data.attestations);
        setRegulPeriods(res.data.regulPeriods);
        setHistory(res.data.history ?? []);
        setReasons(res.data.reasons);
        setSelectableReasons(res.data.selectableReasons ?? res.data.reasons);
        setVorStatus(res.data.vorStatus ?? null);
      })
      .catch(() =>
        openNotification("Erreur", "Impossible de charger les données", "error")
      )
      .finally(() => setLoading(false));
  };

  // ---- Calendar helpers ----

  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = CALENDAR_START_YEAR; y <= currentYear; y++) years.push(y);
    return years;
  };

  const buildStatusMap = () => {
    const map = {}; // key = "YYYY-MM" → "declaration"|"billAttestation"|"attestation"|"regul"

    declarations.forEach((d) => {
      getCoveredMonthKeys(parseInt(d.inputedDate), d.periodType).forEach((k) => {
        if (!map[k]) map[k] = "declaration";
      });
    });

    billAttestations.forEach((a) => {
      getCoveredMonthKeys(parseInt(a.inputedDate), a.periodType).forEach((k) => {
        if (!map[k]) map[k] = "billAttestation";
      });
    });

    attestations.forEach((a) => {
      getCoveredMonthKeys(parseInt(a.inputedDate), a.periodType).forEach((k) => {
        if (!map[k]) map[k] = "attestation";
      });
    });

    regulPeriods.forEach((rp) => {
      getCoveredMonthKeys(parseInt(rp.inputedDate), rp.periodType).forEach((k) => {
        if (!map[k]) map[k] = "regul";
      });
    });

    return map;
  };

  const getRegulForMonthKey = (key) =>
    regulPeriods.find((rp) =>
      getCoveredMonthKeys(parseInt(rp.inputedDate), rp.periodType).has(key)
    ) || null;

  const getDeclarationBillsForMonthKey = (key) =>
    declarations.filter((d) =>
      getCoveredMonthKeys(parseInt(d.inputedDate), d.periodType).has(key)
    );

  const getCellMeta = (year, month) => {
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    const now = new Date();
    const currentYear = now.getFullYear();

    if (year > currentYear) {
      return { type: "future", regul: null };
    }

    const statusMap = buildStatusMap();
    const status = statusMap[monthKey];

    if (status === "declaration") {
      return { type: "declaration", regul: null, bills: getDeclarationBillsForMonthKey(monthKey) };
    }
    if (status === "billAttestation") return { type: "billAttestation", regul: null };
    if (status === "attestation") return { type: "attestation", regul: null };
    if (status === "regul") {
      return { type: "regul", regul: getRegulForMonthKey(monthKey) };
    }

    return { type: "empty", regul: null };
  };

  // ---- Cell rendering ----

  const CELL_STYLES = {
    future:          { background: "#fafafa", color: "#ccc", cursor: "default" },
    declaration:     { background: "#f6ffed", color: "#52c41a", cursor: "default" },
    billAttestation: { background: "#f9f0ff", color: "#722ed1", cursor: "default" },
    attestation:     { background: "#e6f7ff", color: "#1890ff", cursor: "default" },
    regul:           { background: "#fff7e6", color: "#fa8c16", cursor: "pointer" },
    empty:           { background: "#fff", color: "#999", cursor: "pointer" },
  };

  const CELL_CONTENT = {
    future:          "",
    declaration:     <CheckOutlined style={{ color: "#52c41a" }} />,
    billAttestation: <CheckOutlined title="Attestation (validée)" style={{ color: "#722ed1" }} />,
    attestation:     <CheckOutlined title="Attestation (en attente)" style={{ color: "#1890ff" }} />,
    regul:           <span style={{ fontWeight: 700, color: "#fa8c16" }}>R</span>,
    empty:           <PlusOutlined style={{ opacity: 0.3 }} />,
  };

  const handleCellClick = (year, month) => {
    const { type, regul } = getCellMeta(year, month);
    if (type === "future" || type === "declaration" || type === "billAttestation" || type === "attestation") return;

    setSelectedCell({ year, month });
    if (type === "regul" && regul) {
      setEditingRegul(regul);
      form.setFieldsValue({
        periodType: regul.periodType,
        reason: regul.reason,
        reasonComment: regul.reasonComment,
      });
    } else {
      setEditingRegul(null);
      form.resetFields();
      form.setFieldValue("periodType", "monthly");
    }
    setDrawerVisible(true);
  };

  // ---- Form submit ----

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    const { year, month } = selectedCell;

    // Option "fill" : créer toutes les réguls manquantes de l'année
    if (values.periodType === "fill") {
      axios
        .post(
          `${BACKEND_URL}/api/admin/regul-periods/fill-year`,
          {
            companyId: company.id,
            year,
            reason: values.reason,
            reasonComment: values.reasonComment || null,
          },
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        )
        .then((res) => {
          const n = res.data.created;
          openNotification(
            "Succès",
            n > 0
              ? `${n} régularisation(s) créée(s) pour ${year}`
              : `Aucun mois à régulariser en ${year}`,
            "success"
          );
          setDrawerVisible(false);
          fetchData();
        })
        .catch((err) => {
          const msg = err.response?.data?.error || "Une erreur est survenue";
          openNotification("Erreur", msg, "error");
        })
        .finally(() => setSubmitting(false));
      return;
    }

    // Option normale : mensuel
    const inputedDate = Math.floor(new Date(year, month - 1, 1).getTime() / 1000);
    const payload = {
      companyId: company.id,
      inputedDate,
      periodType: values.periodType,
      reason: values.reason,
      reasonComment: values.reasonComment || null,
    };

    const request = editingRegul
      ? axios.put(
          `${BACKEND_URL}/api/admin/regul-periods/${editingRegul.id}`,
          payload,
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        )
      : axios.post(`${BACKEND_URL}/api/admin/regul-periods`, payload, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

    request
      .then(() => {
        openNotification(
          "Succès",
          editingRegul ? "Régularisation modifiée" : "Régularisation créée",
          "success"
        );
        setDrawerVisible(false);
        fetchData();
      })
      .catch((err) => {
        const msg = err.response?.data?.error || "Une erreur est survenue";
        openNotification("Erreur", msg, "error");
      })
      .finally(() => setSubmitting(false));
  };

  const handleDelete = () => {
    if (!editingRegul) return;
    setSubmitting(true);
    axios
      .delete(`${BACKEND_URL}/api/admin/regul-periods/${editingRegul.id}`, {
        withCredentials: true,
      })
      .then(() => {
        openNotification("Succès", "Régularisation supprimée", "success");
        setDrawerVisible(false);
        fetchData();
      })
      .catch(() => openNotification("Erreur", "Impossible de supprimer", "error"))
      .finally(() => setSubmitting(false));
  };

  // ---- CSV export (même endpoint que la page liste des déclarations) ----

  const handleExportCsv = () => {
    setExporting(true);
    openNotification(
      "Export CSV",
      "Votre téléchargement a débuté en arrière plan. Cela peut prendre un peu de temps.",
      "info"
    );
    axios
      .get(`${BACKEND_URL}/api/export/excel?`, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        params: { user: `c_${company?.id}`, includeRegul: "1" },
        responseType: "blob",
      })
      .then((response) => {
        const href = URL.createObjectURL(response?.data);
        const link = document.createElement("a");
        link.href = href;
        const date = new Date();
        link.setAttribute(
          "download",
          `declarations_${company?.membershipCode || membershipCode}_${date.getDate()}_${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}_${date.getUTCFullYear()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch(() => openNotification("Erreur", "Impossible de générer l'export", "error"))
      .finally(() => setExporting(false));
  };

  // ---- History table ----

  const historyColumns = [
    {
      title: "Date/heure",
      dataIndex: "performedAt",
      key: "performedAt",
      render: (ts) => formatTs(ts),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (action) => {
        const labels = { created: "Création", updated: "Modification", deleted: "Suppression" };
        const colors = { created: "green", updated: "orange", deleted: "red" };
        return <Tag color={colors[action]}>{labels[action] || action}</Tag>;
      },
    },
    {
      title: "Administrateur",
      dataIndex: "performedBy",
      key: "performedBy",
    },
    {
      title: "Période",
      key: "period",
      render: (_, record) => {
        if (!record.inputedDate) return "—";
        const d = new Date(record.inputedDate * 1000);
        const typeLabel = PERIOD_TYPE_LABELS[record.periodType] || record.periodType || "";
        return `${typeLabel} — ${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
      },
    },
    {
      title: "Raison",
      key: "reason",
      render: (_, record) => {
        if (!record.reason) return "—";
        const label = reasons[record.reason] ?? record.reason;
        return record.reasonComment ? `${label} : ${record.reasonComment}` : label;
      },
    },
  ];

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const years = getYearRange();
  const currentYear = new Date().getFullYear();

  return (
    <div className="block-right" style={{ padding: "20px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/users")} />
        <div>
          <h2 style={{ margin: 0 }}>
            Régularisations — {company?.companyName}
          </h2>
          <span style={{ color: "#888" }}>Code adhérent : {company?.membershipCode}</span>
          {vorStatus && (
            <div style={{ marginTop: 6 }}>
              <Tag color={vorStatus.declarations}>
                VOR déclarations : {VOR_LABELS[vorStatus.declarations] || vorStatus.declarations}
              </Tag>
              <Tag color={vorStatus.payments}>
                VOR paiements : {VOR_LABELS[vorStatus.payments] || vorStatus.payments}
              </Tag>
            </div>
          )}
        </div>
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={handleExportCsv}
          style={{ marginLeft: "auto" }}
        >
          Exporter CSV
        </Button>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <Tag color="green">✓ Déclaration</Tag>
        <Tag color="purple">✓ Attestation (validée)</Tag>
        <Tag color="blue">✓ Attestation (en attente)</Tag>
        <Tag color="orange">[R] Régularisation</Tag>
        <Tag color="default">+ Cliquer pour régulariser</Tag>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 12, color: "#888" }}>
          Paiement :
          {Object.entries(PAYED_LABELS).map(([status, label]) => (
            <span key={status} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: PAYED_DOT_COLORS[status],
                  display: "inline-block",
                }}
              />
              {label}
            </span>
          ))}
        </span>
      </div>

      {/* Calendar grid */}
      <div style={{ overflowX: "auto", marginBottom: 32 }}>
        <table style={{ borderCollapse: "collapse", minWidth: 600 }}>
          <thead>
            <tr>
              <th style={{ padding: "6px 12px", textAlign: "left", color: "#888", minWidth: 60 }}>
                Année
              </th>
              {MONTH_NAMES.map((name) => (
                <th key={name} style={{ padding: "6px 8px", textAlign: "center", color: "#888", minWidth: 48 }}>
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {years.map((year) => (
              <tr key={year}>
                <td style={{ padding: "4px 12px", fontWeight: 600, color: "#333" }}>
                  {year}
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                  const { type, regul, bills } = getCellMeta(year, month);
                  const style = CELL_STYLES[type];
                  const content = CELL_CONTENT[type];
                  const isClickable = type === "regul" || type === "empty";

                  const payedStatus =
                    type === "declaration" && bills?.length ? worstPayedStatus(bills) : null;

                  const cell = (
                    <td
                      key={month}
                      onClick={() => isClickable && handleCellClick(year, month)}
                      style={{
                        ...style,
                        padding: "6px",
                        textAlign: "center",
                        border: "1px solid #f0f0f0",
                        minWidth: 48,
                        height: 36,
                        userSelect: "none",
                        transition: "background 0.15s",
                        position: "relative",
                      }}
                    >
                      {content}
                      {payedStatus && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: 2,
                            right: 2,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: PAYED_DOT_COLORS[payedStatus],
                          }}
                        />
                      )}
                    </td>
                  );

                  if (type === "declaration" && bills?.length) {
                    return (
                      <Tooltip
                        key={month}
                        title={
                          <div>
                            {bills.map((b, idx) => (
                              <div
                                key={b.id}
                                style={{ marginBottom: idx < bills.length - 1 ? 8 : 0 }}
                              >
                                <div>Type : {PERIOD_TYPE_LABELS[b.periodType] || b.periodType || "—"}</div>
                                <div>N° document : {b.documentNumber || "—"}</div>
                                <div>Montant : {b.feeAmountLocale || "—"}</div>
                                <div>Paiement : {PAYED_LABELS[b.payed] || b.payed || "—"}</div>
                                <div>Créée le : {formatTs(b.creationDate) || "—"}</div>
                                <div>Payée le : {formatTs(b.paymentDate) || "—"}</div>
                              </div>
                            ))}
                          </div>
                        }
                      >
                        {cell}
                      </Tooltip>
                    );
                  }

                  if (type === "regul" && regul) {
                    return (
                      <Tooltip
                        key={month}
                        title={`${reasons[regul.reason] || regul.reason}${regul.reasonComment ? ` : ${regul.reasonComment}` : ""} — Créé par ${regul.createdBy || "?"}`}
                      >
                        {cell}
                      </Tooltip>
                    );
                  }
                  return React.cloneElement(cell, { key: month });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Lien vers la liste des déclarations filtrée sur l'entreprise */}
      <Button
        type="link"
        icon={<UnorderedListOutlined />}
        style={{ paddingLeft: 0, marginBottom: 16 }}
        onClick={() =>
          navigate("/tableDeclaration", {
            state: { user: `c_${company?.id}`, userLabel: company?.companyName },
          })
        }
      >
        Voir les déclarations de cette entreprise
      </Button>

      {/* History table */}
      <h3>Historique des régularisations</h3>
      <Table
        dataSource={history.map((log, i) => ({ key: log.id ?? i, ...log }))}
        columns={historyColumns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="small"
        locale={{ emptyText: "Aucune régularisation pour cette entreprise" }}
      />

      {/* Add/Edit Drawer */}
      <Drawer
        title={
          editingRegul
            ? `Modifier — ${selectedCell ? `${String(selectedCell.month).padStart(2, "0")}/${selectedCell.year}` : ""}`
            : `Ajouter une régularisation — ${selectedCell ? `${String(selectedCell.month).padStart(2, "0")}/${selectedCell.year}` : ""}`
        }
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={400}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {editingRegul && (
              <Popconfirm
                title="Supprimer cette régularisation ?"
                description="L'enregistrement sera conservé (soft delete)."
                onConfirm={handleDelete}
                okText="Supprimer"
                cancelText="Annuler"
                icon={<QuestionCircleOutlined style={{ color: "red" }} />}
              >
                <Button danger icon={<DeleteOutlined />} loading={submitting}>
                  Supprimer
                </Button>
              </Popconfirm>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Button onClick={() => setDrawerVisible(false)}>Annuler</Button>
              <Button type="primary" onClick={handleSubmit} loading={submitting}>
                Enregistrer
              </Button>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="periodType"
            label="Type de période"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Select>
              <Option value="monthly">Mensuel</Option>
              {!editingRegul && selectedCell && (
                <Option value="fill">
                  Remplir l'année {selectedCell.year}
                </Option>
              )}
            </Select>
          </Form.Item>

          {watchedPeriodType === "fill" && selectedCell && (
            <div style={{ color: "#888", fontSize: 12, marginBottom: 16, padding: "8px 12px", background: "#fafafa", borderRadius: 4 }}>
              Créera des régularisations mensuelles pour tous les mois non couverts de {selectedCell.year}.
            </div>
          )}

          <Form.Item
            name="reason"
            label="Raison"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Select placeholder="Sélectionner une raison">
              {Object.entries(selectableReasons).map(([value, label]) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
              {editingRegul && !selectableReasons[editingRegul.reason] && (
                <Option key={editingRegul.reason} value={editingRegul.reason} disabled>
                  {editingRegul.reasonLabel}
                </Option>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.reason !== curr.reason}
          >
            {({ getFieldValue }) =>
              getFieldValue("reason") === "other" ? (
                <Form.Item
                  name="reasonComment"
                  label="Précisez"
                  rules={[{ required: true, message: "Précisez la raison" }]}
                >
                  <Input.TextArea rows={3} maxLength={1000} showCount />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>

        {editingRegul && (
          <div style={{ marginTop: 24, padding: 12, background: "#fafafa", borderRadius: 6, fontSize: 12, color: "#888" }}>
            <div>Créé le {formatTs(editingRegul.creationDate)} par {editingRegul.createdBy}</div>
            {editingRegul.lastUpdateDate !== editingRegul.creationDate && (
              <div>Modifié le {formatTs(editingRegul.lastUpdateDate)} par {editingRegul.updatedBy}</div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
